import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { FlatTreeNode } from 'types';
import { COLORS, Checkbox, Tag } from 'pvs-design-system';
import { Box } from '@mui/material';

// ── Internal tree node shape ──────────────────────────────────────────
export interface TreeNode extends FlatTreeNode {
  children: TreeNode[];
}

// ── Collect all descendant IDs from a TreeNode ──────────────────────
function collectTreeDescendantIds(node: TreeNode): number[] {
  const result: number[] = [];
  const queue = [...node.children];
  while (queue.length > 0) {
    const c = queue.shift()!;
    result.push(c.id);
    queue.push(...c.children);
  }
  return result;
}

// ── Recursive node renderer ────────────────────────────────────────────
export interface TreeNodeItemProps {
  node: TreeNode;
  // Single-select
  selectedId?: number | null;
  onSelect?: (id: number) => void;
  // Multi-select
  multiSelect?: boolean;
  selectedIds?: Set<number>;
  onToggle?: (id: number) => void;
  depth: number;
  /** Root-level nodes start expanded when true (used by inline variant) */
  startExpanded?: boolean;
  /** Lazy-load callback — called when expanding a node that has hasChildren=true but no loaded children */
  onLoadChildren?: (nodeId: number) => Promise<void>;
  /** Set of ancestor IDs of the selected node — these auto-expand to reveal the selection */
  expandedAncestors?: Set<number>;
  /** When true, display the locationType as a Tag after the name */
  showLocationType?: boolean;
  /** When true, append a [selected/total] sublocation count after the name */
  showCount?: boolean;
  /** Ids counted as "selected" for the [selected/total] count (defaults to selectedIds) */
  countIds?: Set<number>;
  /** All flat nodes — needed for computing descendant counts with lazy-loaded trees */
  allFlatNodes?: FlatTreeNode[];
}

const TreeNodeItemBase: React.FC<TreeNodeItemProps> = ({
  node,
  selectedId,
  onSelect,
  multiSelect = false,
  selectedIds,
  onToggle,
  depth,
  startExpanded = false,
  onLoadChildren,
  expandedAncestors,
  showLocationType = false,
  showCount = false,
  countIds,
  allFlatNodes,
}) => {
  const isAncestorOfSelected = expandedAncestors?.has(node.id) ?? false;
  // A node is expandable if it has loaded children OR the API indicated hasChildren=true
  const hasLoadedChildren = node.children.length > 0;
  const canExpand = hasLoadedChildren || (node.hasChildren === true);

  const shouldAutoExpand = (startExpanded && depth === 0) || isAncestorOfSelected;
  // Only start expanded when children are actually loaded — prevents the arrow
  // showing as "expanded" (down) while children haven't arrived yet, which
  // forced the user to click twice (once to collapse, once to truly expand).
  const [expanded, setExpanded] = useState(shouldAutoExpand && hasLoadedChildren);
  const [loading, setLoading] = useState(false);

  // When children finish loading (lazy-load or init data arriving), auto-expand
  // nodes that are supposed to start expanded.
  useEffect(() => {
    if (shouldAutoExpand && hasLoadedChildren) {
      setExpanded(true);
    }
  }, [hasLoadedChildren, shouldAutoExpand]);
  const isSelected = multiSelect
    ? (selectedIds?.has(node.id) ?? false)
    : node.id === selectedId;

  // Compute descendant info for status icon and count display.
  const descendantIds = useMemo(() => {
    if (!allFlatNodes) return collectTreeDescendantIds(node);
    const childrenMap = new Map<number, number[]>();
    for (const n of allFlatNodes) {
      if (n.idParent != null) {
        const siblings = childrenMap.get(n.idParent) ?? [];
        siblings.push(n.id);
        childrenMap.set(n.idParent, siblings);
      }
    }
    const result: number[] = [];
    const queue = [node.id];
    while (queue.length > 0) {
      const current = queue.shift()!;
      for (const childId of childrenMap.get(current) ?? []) {
        if (childId !== node.id) {
          result.push(childId);
          queue.push(childId);
        }
      }
    }
    return result;
  }, [allFlatNodes, node]);

  const selectedDescCount = useMemo(() => {
    const countSet = countIds ?? selectedIds;
    if (!countSet) return 0;
    return descendantIds.filter((id) => countSet.has(id)).length;
  }, [countIds, selectedIds, descendantIds]);

  const handleRowClick = () => {
    if (multiSelect) {
      onToggle?.(node.id);
    } else {
      onSelect?.(node.id);
    }
  };

  const handleExpandToggle = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (expanded) {
      setExpanded(false);
      return;
    }
    // Lazy-load children if they haven't been fetched yet
    if (!hasLoadedChildren && node.hasChildren && onLoadChildren) {
      setLoading(true);
      try {
        await onLoadChildren(node.id);
      } finally {
        setLoading(false);
      }
    }
    setExpanded(true);
  }, [expanded, hasLoadedChildren, node.hasChildren, node.id, onLoadChildren]);

  return (
    <li style={{ listStyle: 'none' }} role="none">
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          paddingLeft: `${12 + depth * 20}px`,
          paddingRight: 0,
          paddingTop: 6,
          paddingBottom: 6,
          cursor: 'pointer',
          fontSize: 16,
          color: isSelected && !multiSelect ? COLORS.purple[800] : COLORS.gray[900],
          backgroundColor: isSelected && !multiSelect ? 'rgba(114, 35, 98, 0.1)' : undefined,
          fontWeight: isSelected && !multiSelect ? 600 : undefined,
          borderRadius: 0,
          transition: 'background-color 0.1s',
          userSelect: 'none',
        }}
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={canExpand ? expanded : undefined}
        tabIndex={0}
        onClick={handleRowClick}
        onMouseEnter={(e) => { if (!(isSelected && !multiSelect)) (e.currentTarget as HTMLElement).style.backgroundColor = COLORS.purple[100]; }}
        onMouseLeave={(e) => { if (!(isSelected && !multiSelect)) (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (multiSelect) {
              onToggle?.(node.id);
            } else {
              onSelect?.(node.id);
            }
          }
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, flexShrink: 0 }}>
          {canExpand && !loading && (
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: COLORS.gray[700],
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
              }}
              aria-label={expanded ? `Collapse ${node.name}` : `Expand ${node.name}`}
              onClick={handleExpandToggle}
              tabIndex={-1}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="currentColor"
                aria-hidden="true"
                style={{
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s ease',
                }}
              >
                <path d="M3 1.5 L7 5 L3 8.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          {loading && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ animation: 'spin 0.8s linear infinite' }}>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <circle cx="12" cy="12" r="10" stroke={COLORS.gray[200]} strokeWidth="3" />
              <path d="M12 2a10 10 0 0 1 10 10" stroke={COLORS.purple[700]} strokeWidth="3" strokeLinecap="round" />
            </svg>
          )}
        </span>

        {multiSelect && (
          <Checkbox
            id={`tree-node-${node.id}`}
            checked={isSelected}
            onChange={() => onToggle?.(node.id)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            aria-label={node.name}
            sx={{ padding: 0, marginRight: '2px', '& .MuiSvgIcon-root': { fontSize: 18 } }}
          />
        )}

        <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
          <span>{node.name}</span>
          {showLocationType && node.locationType && (
            <Tag
              label={node.locationType}
              size="small"
              variant="outlined"
              sx={{ borderColor: COLORS.gray[400], '& span': { color: COLORS.gray[900] } }}
            />
          )}
          {showCount && descendantIds.length > 0 && (
            <span style={{ color: COLORS.gray[400], fontSize: 13 }}>
              [{selectedDescCount}/{descendantIds.length}]
            </span>
          )}
        </span>
      </Box>

      {hasLoadedChildren && expanded && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} role="group">
          {node.children.map((child) => (
            <TreeNodeItem
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              multiSelect={multiSelect}
              selectedIds={selectedIds}
              onToggle={onToggle}
              depth={depth + 1}
              startExpanded={startExpanded}
              onLoadChildren={onLoadChildren}
              expandedAncestors={expandedAncestors}
              showLocationType={showLocationType}
              showCount={showCount}
              countIds={countIds}
              allFlatNodes={allFlatNodes}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export const TreeNodeItem = React.memo(TreeNodeItemBase);
